import * as Notifications from 'expo-notifications';

// shouldPlaySound/shouldShowBanner/shouldShowList apply in ALL app states,
// including foregrounded — expo-notifications does not auto-suppress
// foreground banners. METIS/THEMIS timers already play their own in-app
// audio (Audio.Sound) + Vibration the instant their own JS interval detects
// expiry, so a scheduled OS notification firing at the same wall-clock
// moment while the app is foregrounded would double the audible cue (chime
// + banner sound). Rather than relying on notification-handler suppression
// (which would also silence backgrounded/killed-app delivery — the whole
// point of this scheduling), callers cancel the scheduled notification from
// inside their own in-app expiry handler (the common case: app is
// foregrounded, JS timer fires first). If the app is backgrounded/killed,
// the in-app handler never runs, the cancel never happens, and the OS
// notification is the ONLY cue — exactly once. This is the
// non-duplicating option: cancel-on-in-app-fire, not handler suppression.
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
  }),
});

// Phase 5 local-notification slice. Remote push needs a development build +
// APNs/FCM credentials (see docs/mobile/RELEASE_CHECKLIST.md).
export async function scheduleTestReminder(): Promise<string> {
  const { status } = await Notifications.requestPermissionsAsync();
  if (status !== 'granted') {
    return 'Notification permission denied — enable it in system Settings.';
  }
  await Notifications.scheduleNotificationAsync({
    content: {
      title: 'OLMPS',
      body: 'Test reminder — your notification pipeline works.',
    },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
      seconds: 10,
    },
  });
  return 'Scheduled — background the app and watch for it in ~10 seconds.';
}

// ── Shared timer-alert helper (METIS duration/pomodoro timers, THEMIS step
// timers) ── schedules a local notification for a wall-clock deadline so the
// alert still fires if the app is backgrounded or killed before then. Mirrors
// scheduleTestReminder's permission-request pattern but is silent (no
// user-facing string) since callers fire it from timer-start code paths, not
// a settings button — a denied/undetermined permission simply means no OS
// notification is scheduled, and the in-app audio/vibration cue (unaffected
// by this) is still the primary alert while foregrounded.
export async function scheduleTimerNotification(
  deadline: Date | number,
  title: string,
  body: string
): Promise<string | null> {
  const { status } = await Notifications.requestPermissionsAsync();
  if (status !== 'granted') return null;
  const date = typeof deadline === 'number' ? new Date(deadline) : deadline;
  const seconds = Math.max(1, Math.round((date.getTime() - Date.now()) / 1000));
  return Notifications.scheduleNotificationAsync({
    content: { title, body },
    trigger: {
      type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
      seconds,
    },
  });
}

export async function cancelTimerNotification(id: string | null | undefined): Promise<void> {
  if (!id) return;
  try {
    await Notifications.cancelScheduledNotificationAsync(id);
  } catch {
    // Already fired/cancelled — non-fatal.
  }
}
